import { EMPTY_BRIEF, PROJECT_TYPES, type Brief, type ProjectType } from './types';

export const DETAIL_QUESTIONS: Record<ProjectType, string> = {
  App: 'Which platform(s) should it run on?',
  Website: 'What are the main pages?',
  Game: 'What genre is it?',
  Agent: 'What should it be able to do?',
  Tool: 'What is the core function?',
  Integration: 'What does it connect to?',
};

export const DETAIL_FIELDS: Record<ProjectType, { label: string; placeholder: string }> = {
  App: { label: 'Platform', placeholder: 'Web, mobile, desktop…' },
  Website: { label: 'Key pages', placeholder: 'Home, pricing, docs…' },
  Game: { label: 'Genre', placeholder: 'Action, puzzle, strategy…' },
  Agent: { label: 'Capabilities', placeholder: 'Answer questions, run tasks…' },
  Tool: { label: 'Core function', placeholder: 'Analyze, convert, generate…' },
  Integration: { label: 'Target service', placeholder: 'Slack, GitHub, Stripe…' },
};

export const WELCOME =
  "Hey! I'm the Sourcetape composer. What do you want to build? Describe it in a sentence or two.";

export const TYPE_QUESTION = 'Got it. Which type best fits your project?';
export const NAME_QUESTION = 'Nice choice. What should we call it?';
export const FEATURES_QUESTION = 'Last one. Any must-have features? List a few, comma-separated.';

export function nowTime() {
  const d = new Date();
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function normalizeType(text: string): ProjectType {
  const lower = text.toLowerCase();
  const exact = PROJECT_TYPES.find((t) => t.toLowerCase() === lower);
  if (exact) return exact;
  const byKeyword = PROJECT_TYPES.find(
    (t) => lower.includes(t.toLowerCase()) || t.toLowerCase().includes(lower),
  );
  return byKeyword || 'App';
}

export function seedBriefFromPrompt(prompt: string): { brief: Brief; step: number } | null {
  const trimmed = prompt.trim();
  if (!trimmed) return null;
  return { brief: { ...EMPTY_BRIEF, description: trimmed }, step: 1 };
}

export function applyInterviewTurn(
  step: number,
  brief: Brief,
  text: string,
): { brief: Brief; step: number; assistant: string } | null {
  const trimmed = text.trim();
  if (!trimmed || step >= 5) return null;

  let nextBrief = { ...brief };
  let chosenType = brief.type;
  if (step === 0) nextBrief = { ...nextBrief, description: trimmed };
  else if (step === 1) {
    chosenType = normalizeType(trimmed);
    nextBrief = { ...nextBrief, type: chosenType };
  } else if (step === 2) nextBrief = { ...nextBrief, name: trimmed };
  else if (step === 3) nextBrief = { ...nextBrief, detail: trimmed };
  else if (step === 4) nextBrief = { ...nextBrief, features: trimmed };

  const next = step + 1;
  let assistant = '';
  if (next === 1) assistant = TYPE_QUESTION;
  else if (next === 2) assistant = NAME_QUESTION;
  else if (next === 3) assistant = DETAIL_QUESTIONS[chosenType ?? 'App'];
  else if (next === 4) assistant = FEATURES_QUESTION;
  else if (next === 5) {
    const label = nextBrief.name || 'Your project';
    assistant = `Your brief is ready. "${label}" is set as a ${chosenType}. Review it on the right, then continue building.`;
  }
  return { brief: nextBrief, step: next, assistant };
}

export function briefOutcomeDescription(brief: Brief, capabilityNames: string[]): string {
  const caps = capabilityNames.length > 0 ? `Capabilities: ${capabilityNames.join(', ')}.` : '';
  return [brief.description, brief.detail, brief.features, caps].filter(Boolean).join('\n');
}
